// AudioPlayer.js
// import 'swiper/css';
import 'swiper/css/navigation';
import React, { useState, useEffect, forwardRef, useRef, useCallback } from 'react';
import classNames from 'classnames';
import useEpisodePlayersContext from '@mainHooks/useEpisodePlayersContext';
import { AppProviderPlayer } from '@audioPlayer/context/PlayersContext';
import Rewind30SecButton from '@audioPlayer/PlayerControls/Rewind30SecButton';
import SkipAhead30secButton from '@audioPlayer/PlayerControls/SkipAhead30secButton';
import ProgressBar from '@audioPlayer/PlayerControls/ProgressBar';
import EpisodeLength from '@audioPlayer/PlayerControls/EpisodeLength';
import PlayPauseButton from '@audioPlayer/PlayerControls/PlayPauseButton';
import DownloadButton from '@audioPlayer/PlayerInterface/DownloadButton';
import DescriptionButton from '@audioPlayer/PlayerInterface/DescriptionButton';
import EpisodeLengthMobile from '@audioPlayer/PlayerControls/EpisodeLengthMobile';
import Description from '@playerInterface/Description';
import Subscribe from '@playerInterface/Subscribe';
import Image from '@playerInterface/Image';
import Share from '@playerInterface/Share';
import Embed from '@playerInterface/Embed';

const AudioPlayer = forwardRef((props, ref) => {
    let { play, episodeIndex, handleClick } = props;
    const { data, setActiveIndex, activeEpisodeIndex, setClickedButtonIndex, clickedButtonIndex, scrollDisabled } =
        useEpisodePlayersContext();

    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [audioDuration, setAudioDuration] = useState(0);

    const [isAudioLoaded, setIsAudioLoaded] = useState(false);
    const refPLayer = useRef(null);
    const { episodeSrc, episodeTitle, episodeDescription, duration } = data.episodes[episodeIndex];

    const [isSliderProgress, setIsSliderProgress] = useState(false);

    // play by id
    const playId = useCallback(
        (idPlayer) => {
            setIsPlaying(!isPlaying);
            setClickedButtonIndex(null);
            setActiveIndex(idPlayer);
            setTimeout(() => {
                play(idPlayer);
                setClickedButtonIndex(true);
            }, 600);
        },
        [isPlaying, setClickedButtonIndex, setActiveIndex, setIsSliderProgress, play]
    );

    const buttonDisabled = useCallback(() => {
        if (activeEpisodeIndex !== episodeIndex) {
            return clickedButtonIndex !== episodeIndex;
        } else {
            return undefined;
        }
    }, [activeEpisodeIndex, episodeIndex, clickedButtonIndex, ref]);

    const handleTimeUpdate = useCallback(() => {
        setCurrentTime(Math.round(ref.current.currentTime));
    }, [setCurrentTime]);

    const loadAudioFile = useCallback(() => {
        return new Promise((resolve, reject) => {
            const audioElement = new Audio(episodeSrc);
            ref.current = audioElement;

            const handleLoadedData = () => {
                setIsAudioLoaded(true);
                setIsSliderProgress(false);
                resolve(ref.current);
            };

            audioElement.controls = true;
            audioElement.autoplay = false;

            audioElement.addEventListener('timeupdate', handleTimeUpdate);

            audioElement.addEventListener('loadedmetadata', () => {
                setIsAudioLoaded(true);
                setIsSliderProgress(false);
                resolve(ref.current);
            });

            audioElement.addEventListener('error', (error) => {
                reject(error);
            });
        });
    }, [setIsAudioLoaded, setIsSliderProgress]);

    useEffect(() => {
        setTimeout(() => {
            if (ref.current && !isNaN(ref.current.duration)) {
                setIsAudioLoaded(true);
            }
        }, 300);
    }, [ref.current]);

    const decodeHtml = (html) => {
        const txt = document.createElement("textarea");
        txt.innerHTML = html;
        return txt.value;
    };

    const title = data.podcastTitle ?? data.episodes[episodeIndex]?.podcastTitle;
    const decodedTitle = decodeHtml(title);

    const handlePlaybackEnd = () => {
        if (!isNaN(ref.current?.duration)) {
            const currentPlaybackTime = Math.round(ref.current.currentTime);
            const episodeDuration = Math.round(ref.current.duration);

            if (currentPlaybackTime >= episodeDuration) {
                if (ref.current) {
                    ref.current.pause();
                    ref.current.currentTime = 0;

                    if (data.episodes.length === episodeIndex + 1) {
                        setActiveIndex(null);
                        setIsPlaying(!isPlaying);
                        setClickedButtonIndex(null);
                        return;
                    }
                    playId(episodeIndex + 1);
                }
            }
        }
    };

    useEffect(() => {
        handlePlaybackEnd();
    }, [currentTime]);

    useEffect(() => {
        if (!scrollDisabled && refPLayer.current?.classList.contains('episode-player-activate')) {
            const element = refPLayer.current;
            const offset = 88;

            const top = element.getBoundingClientRect().top + window.pageYOffset - offset;

            window.scrollTo({
                top,
                behavior: 'smooth',
            });
        }
    }, [handleClick, isAudioLoaded]);

    const itemMainClass = classNames('player-app__item-main', {
        'episode-player-activate': isAudioLoaded && !buttonDisabled(),
        'episode-player-deactivate': !isAudioLoaded || buttonDisabled(),
    });

    return (
        <AppProviderPlayer episodeIndex={episodeIndex}>
            <div ref={refPLayer} className={itemMainClass}>
                <>
                    <div className="player-app__item-ready">
                        <Image />
                        <div className="player-app__col">
                            <PlayPauseButton
                                episodeId={episodeIndex}
                                loadAudioFile={loadAudioFile}
                                handleClick={handleClick}
                                setIsPlaying={setIsPlaying}
                                isSliderProgress={isSliderProgress}
                                updateDuration={setAudioDuration}
                                setIsSliderProgress={setIsSliderProgress}
                                ref={ref}
                            />
                            <EpisodeLengthMobile currentTime={currentTime} />
                        </div>
                        <div className="player-app__col">
                            <div className="player-app__podcast-title">
                                {decodedTitle}
                            </div>
                            <div className="player-app__episode-title">{episodeTitle}</div>
                            <ProgressBar
                                buttonDisabled={buttonDisabled}
                                currentTime={currentTime}
                                ref={ref}
                                handleClick={handleClick}
                                setIsPlaying={setIsPlaying}
                                setCurrentTime={setCurrentTime}
                                episodeSrc={episodeSrc}
                            />
                            <div className="player-app__controls">
                                <div className="player-app__controls-col">
                                    <Rewind30SecButton buttonDisabled={buttonDisabled} ref={ref} />
                                    <EpisodeLength
                                        currentTime={currentTime}
                                        duration={duration}
                                        audioDuration={audioDuration}
                                        ref={ref}
                                    />
                                    <SkipAhead30secButton buttonDisabled={buttonDisabled} ref={ref} />
                                </div>
                                <div className="player-app__controls-col">
                                    <Subscribe />
                                    <DownloadButton episodeSrc={episodeSrc} />
                                    <Embed />
                                    <Share />
                                </div>
                                <div className="player-app__controls-col">
                                    <DescriptionButton />
                                </div>
                            </div>
                        </div>
                    </div>
                    <Description episodeDescription={episodeDescription} />
                </>
            </div>
        </AppProviderPlayer>
    );
});

export default AudioPlayer;
